import React, { useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, Loading } from 'carbon-components-react';
import { FormError } from 'components';
import { APP_URL, CFCProps, CHANNELS, DraftChannelDataType } from 'types';
import { RootState } from 'redux/root.reducer';
import { selectDraftChannels, selectGmlProjectionItems } from 'redux/channels';
import { Launch16 } from '@carbon/icons-react';
import Utils from 'utilities/helper.util';
import { setSandBoxTestingMode } from 'redux/lodgeEnquiry';
import { useHistory } from 'react-router-dom';
import AdvancedFeaturesTile from '../advancedFeatures';
import { WebhookConfig } from 'components';
import PrimaryContentForm from '../primaryContent';

const WebHookChannelForm: React.FC<Props | any> = (props: Props) => {
  const params = new URLSearchParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const draftChannel: DraftChannelDataType = useSelector(selectDraftChannels);
  const gmlProjectionOptions: any = useSelector(selectGmlProjectionItems);
  const [
    gmlProjectionSelected,
    setGmlProjectionSelected
  ] = useState<Object | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | undefined>();
  const [isFormFieldsDirty, setIsFormFieldsDirty] = useState<boolean>(false);
  const [secureKey, setSecureKey] = useState<string>('');
  const [
    isAdvancedOptionsInvavid,
    setIsAdvancedOptionsInvavid
  ] = useState<boolean>(false);
  const [isAdvancedOptionsDirty, setIsAdvancedOptionsDirty] = useState<boolean>(
    false
  );
  const [isPrimaryContentInvalid, setPrimaryContentInvalid] = useState<boolean>(
    false
  );
  const [isPrimaryContentIsDirty, setIsPrimaryContentDirty] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (draftChannel) {
      setIsLoading(draftChannel.submitting);
    }
  }, [draftChannel]);

  useEffect(() => {
    if (isAdvancedOptionsDirty) {
      setIsFormFieldsDirty(true);
    }
  }, [isAdvancedOptionsDirty]);

  const {
    advancedOptions,
    primaryContentOptions,
    values,
    setFieldValue,
    setFieldTouched,
    errors: fErrors,
    touched: fTouched,
    handleChange,
    handleBlur,
    handleSubmit
  } = props;
  const inputError = ((errors: any, touched: any) => (
    key: 'url' | 'clientKey' | 'gmlProjection'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );
  const changeHandler = (e: any) => {
    const key = Object.keys(e)[0];
    const value = Object.values(e)[0];
    values[key] = value;
    values.WebhookConfiguration = {
      ...values.WebhookConfiguration,
      customHeaders: values['customHeaders']
    };
  };
  const gmlProjectionItems: [] =
    gmlProjectionOptions &&
    gmlProjectionOptions.map((item: any) => ({
      label: item.label,
      value: item.code
    }));

  useEffect(() => {
    setSecureKey(values.clientKey);
    if (values) {
      const { gmlProjection, url, clientKey, customHeaders } = values;

      if (gmlProjection) {
        const gmlVal = gmlProjectionOptions?.filter(
          (item: any) => item.code === gmlProjection
        )[0];
        gmlVal && setGmlProjectionSelected(gmlVal);
      }
      values.WebhookConfiguration = {
        url,
        clientKey: secureKey,
        customHeaders
      };
      setIsFormFieldsDirty(!url || !clientKey);
    }
    setIsFormFieldsDirty(!Utils.isObjectEmpty(fTouched));
  }, [values, gmlProjectionOptions, secureKey, fTouched]);

  // useEffect(() => {
  //   if (draftChannel) {
  //     const isWebHookConfig = draftChannel?.WebhookConfiguration !== null;
  //     const isDirtyForm = draftChannel && isFormFieldsDirty;
  //     setIsSaveDraftBtnDisable(
  //       !(isWebHookConfig && isDirtyForm) || isLoading || false
  //     );
  //     setIsActivateDraftBtnDisable(
  //       (isWebHookConfig && isDirtyForm) || isLoading || true
  //     );
  //   }
  // }, [draftChannel, isLoading, isFormFieldsDirty]);

  const gmlProjectionHandler = (val: any) => {
    values.gmlProjection = val.selectedItem.value;
    const gmlVal = gmlProjectionOptions?.filter(
      (item: any) => item.code === values.gmlProjection
    )[0];
    gmlVal && setGmlProjectionSelected(gmlVal);
    setIsFormFieldsDirty(false);
  };

  const generateKey = (key: string) => {
    setIsFormFieldsDirty(false);
    setFieldTouched('clientKey', true);
    setFieldValue('clientKey', key);
  };

  const sandBoxTestHandler = () => {
    dispatch(setSandBoxTestingMode({ isSandboxTest: true }));
    params.append('isSandboxTest', 'true');
    history.push({
      pathname: APP_URL.LODGE_NEW_ENQUIRY,
      search: '?' + params.toString()
    });
  };

  return (
    <React.Fragment>
      <div className="form-mid--body">
        <h4 className="section__sub-heading">Web hook details</h4>
        <WebhookConfig
          handleChange={handleChange}
          handleBlur={handleBlur}
          values={values}
          errors={fErrors}
          touched={fTouched}
          customHeaderchangeHandler={changeHandler}
          secureKey={secureKey}
          generateKeyHandler={generateKey}
        />
        <hr className="section__divider" />
        {gmlProjectionItems?.length > 0 && (
          <div className="form-section--item">
            <Dropdown
              ariaLabel="Dropdown gml Projection"
              id="gmlProjection"
              items={gmlProjectionItems}
              initialSelectedItem={gmlProjectionSelected}
              selectedItem={gmlProjectionSelected}
              label="Select..."
              titleText="GIS projection"
              onChange={(e) => gmlProjectionHandler(e)}
              onBlur={handleBlur}
            />
            {inputError('gmlProjection')}
          </div>
        )}
        <hr className="section__divider" />
        <PrimaryContentForm
          primaryContentOptions={primaryContentOptions}
          setPrimaryContentInvalid={(flag: boolean) =>
            setPrimaryContentInvalid(flag)
          }
          setPrimaryContentDirty={(flag: boolean) =>
            setIsPrimaryContentDirty(flag)
          }
        />
        <AdvancedFeaturesTile
          advancedOptions={advancedOptions}
          setAdvancedOptionsInvavid={(flag: boolean) =>
            setIsAdvancedOptionsInvavid(flag)
          }
          setAdvancedOptionsDirty={(flag: boolean) =>
            setIsAdvancedOptionsDirty(flag)
          }
        />
        <hr className="section__divider" />
        <div className="form-button--wrapper">
          <Button
            className="reg-submit"
            type="submit"
            kind={'secondary'}
            data-testid="submit"
            size="field"
            onClick={() => {
              values.actionType = 'save-draft';
              handleSubmit(values);
            }}
            disabled={
              (draftChannel &&
              draftChannel.configurationType === CHANNELS.WEBHOOK
                ? !isFormFieldsDirty && !isPrimaryContentIsDirty
                : false) ||
              isLoading ||
              isAdvancedOptionsInvavid ||
              isPrimaryContentInvalid
            }
          >
            {'Save draft'}
          </Button>
          <Button
            renderIcon={Launch16}
            kind="tertiary"
            size="field"
            onClick={() => sandBoxTestHandler()}
            disabled={
              !Utils.isObjectEmpty(fTouched) ||
              !(
                draftChannel &&
                draftChannel.configurationType === CHANNELS.WEBHOOK &&
                !isFormFieldsDirty &&
                !isPrimaryContentIsDirty
              ) ||
              isLoading ||
              isAdvancedOptionsInvavid ||
              isPrimaryContentInvalid
            }
          >
            Test run
          </Button>
          <Button
            className="submit-active"
            type="submit"
            kind={'primary'}
            size="field"
            data-testid="submit-active"
            onClick={() => {
              values.actionType = 'make-active';
              handleSubmit(values);
            }}
            disabled={
              !Utils.isObjectEmpty(fTouched) ||
              !(
                draftChannel &&
                draftChannel.configurationType === CHANNELS.WEBHOOK &&
                !isFormFieldsDirty &&
                !isPrimaryContentIsDirty
              ) ||
              isLoading ||
              isAdvancedOptionsInvavid ||
              isPrimaryContentInvalid
            }
          >
            {'Make Active'}
          </Button>
          {isLoading && (
            <Loading
              small
              description="Active loading indicator"
              withOverlay={false}
            />
          )}
        </div>
        <p className="footer--content">
          Save your draft and use ‘Test run’ to lodge a mock enquiry in Sandbox
          testing mode
        </p>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = ({ referralChannels }: RootState) => ({
  referralChannels
});

const connector = connect(mapStateToProps);

type OwnProps = {
  advancedOptions: any;
  primaryContentOptions: any;
};

type Props = CFCProps<typeof connector> & FormikProps<any> & OwnProps;

WebHookChannelForm.displayName = 'WebHookChannelForm';

export default connector(WebHookChannelForm);
